'use client';

/**
 * <ReviewsList /> — public list of buyer_to_seller reviews for a seller's
 * store + seller reply flow (S2-H9 — SPEC-MARKETPLACE-QA-SMOKE-TEST-2026-10-03
 * item H-9).
 *
 * ROOT CAUSE being fixed: sellers had no way to post a public response to
 * a buyer's review (the Review model's 2 directions are SEPARATE reviews,
 * NOT replies). This component renders each buyer review + the seller's
 * single reply (if any) below it, and exposes a "Responder" button for
 * the seller of the store to post a reply via a textarea dialog.
 *
 * Component shape (presentational + reply flow):
 *   - Props: `reviews: ReviewListItem[]`, `sellerUserId: string | null`
 *     (the seller's User.id — when null, the reply button is never shown).
 *   - The current user is read from `useAuth()` — the "Responder" button
 *     is shown ONLY when `user?.id === sellerUserId` AND the review has
 *     no `sellerReply` yet (matches the S2-H9 spec wording "visible only
 *     when viewing their own store page + review has no reply yet").
 *   - On submit, calls `respondToReview(orderNumber, text)` from
 *     `@/lib/market-reviews-api` (a thin typed wrapper around `apiFetch`).
 *     This avoids growing the god-file `api-client.ts` (TECH-DEBT-REGISTER
 *     §2E — god-files are frozen per CR-CHECKLIST I2).
 *
 * Data fetching: this component is PRESENTATIONAL — the parent (e.g. the
 * store page) supplies the `reviews` array. The actual `GET /api/seller/
 * store/:storeSlug/reviews` public endpoint is a follow-up task (registered
 * in TECH-DEBT-REGISTER to avoid scope creep in this PR — the S2-H9 fix
 * is the reply mechanism, not the listing endpoint).
 *
 * §6 Anti-Invención: NO business logic — the 30-day window + 1-reply
 * invariant + caller-is-seller check live in the backend
 * `review.service.respondToReview`. The component only renders state +
 * forwards the user input.
 *
 * Accessibility:
 *   - Star rating is decorative (`aria-hidden`) — the numeric rating is
 *     exposed via a visually-hidden label.
 *   - The reply dialog has `DialogDescription` for screen readers.
 *   - The reply textarea has `maxLength={500}` + a live char counter.
 *
 * P-083: NO technical refs (SPEC-084, S2-H9, INV-MKT-18) in user-visible
 * strings — only amigable Spanish copy.
 *
 * Spec ref: SPEC-MARKETPLACE-QA-SMOKE-TEST-2026-10-03 item H-9 (frontend
 *           reviews-list.tsx + seller reply rendering + Responder button
 *           + textarea dialog).
 */

import { useCallback, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Star, Loader2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { toastError } from '@/lib/toast-premium';
import { useAuth } from '@/lib/use-auth';
import { formatDate } from '@/lib/format';
import {
  respondToReview,
  ApiClientError,
} from '@/lib/market-reviews-api';
import type { ReviewListItem, SellerReply } from '@profileia/shared';

// ─── Constants ──────────────────────────────────────────────────────────────

/**
 * Seller reply text bounds — S2-H9. Mirrors the backend
 * `SELLER_REPLY_MIN_CHARS` / `SELLER_REPLY_MAX_CHARS` +
 * `respondToReviewSchema` (Zod) + the Mongoose `maxlength: 500`. The UI
 * `maxLength` attribute is a fourth layer of defense-in-depth.
 */
const REPLY_MAX_CHARS = 500;

/**
 * Empty-state copy when the seller has no buyer_to_seller reviews yet.
 */
const EMPTY_STATE_TEXT =
  'Aún no hay reseñas de compradores. Cuando un comprador califique tu servicio, su reseña aparecerá aquí.';

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Render a 5-star row with the filled portion matching `rating`. The
 * stars are decorative (`aria-hidden`) — the accessible label is rendered
 * as a visually-hidden span next to the stars so screen readers announce
 * "X de 5 estrellas".
 */
function StarsRow({ rating }: { rating: 1 | 2 | 3 | 4 | 5 | number }) {
  return (
    <div className="flex items-center gap-0.5" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((value) => {
        const isFilled = rating >= value;
        return (
          <Star
            key={value}
            className={`size-4 ${
              isFilled
                ? 'fill-amber-400 text-amber-400'
                : 'fill-transparent text-muted-foreground/40'
            }`}
          />
        );
      })}
      <span className="sr-only">{`${rating} de 5 estrellas`}</span>
    </div>
  );
}

/**
 * Compute the buyer's display initials for the avatar fallback (max 2
 * uppercase letters). Falls back to '?' when no name is available.
 */
function initialsOf(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    return parts[0]!.slice(0, 2).toUpperCase();
  }
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

// ─── Seller reply subdoc (rendered below each buyer review) ─────────────────

/**
 * Render the seller's existing reply below the buyer review (S2-H9). The
 * reply is read-only — edits/deletes are NOT supported (1 reply per
 * review, append-once — mirrors Airbnb/MercadoLibre/Etsy semantics).
 */
function SellerReplyBlock({ reply }: { reply: SellerReply }) {
  return (
    <div className="mt-3 rounded-md border-l-2 border-primary/40 bg-muted/40 p-3">
      <div className="mb-1 flex items-center gap-2">
        <MessageCircle
          className="size-4 text-primary"
          aria-hidden="true"
        />
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Respuesta del vendedor
        </span>
        <span className="ml-auto text-[11px] text-muted-foreground">
          {formatDate(reply.repliedAt, { dateStyle: 'medium' })}
        </span>
      </div>
      <p className="whitespace-pre-wrap text-sm leading-relaxed">
        {reply.text}
      </p>
    </div>
  );
}

// ─── Reply dialog (textarea + submit) ───────────────────────────────────────

/**
 * Reply dialog state — the orderNumber of the review being replied to
 * (null = dialog closed). The text state is reset each time the dialog
 * opens.
 */
interface ReplyDialogState {
  orderNumber: string | null;
  text: string;
  submitting: boolean;
}

const INITIAL_DIALOG_STATE: ReplyDialogState = {
  orderNumber: null,
  text: '',
  submitting: false,
};

// ─── Component ──────────────────────────────────────────────────────────────

export interface ReviewsListProps {
  /** Reviews to render (buyer_to_seller — already filtered by the parent). */
  reviews?: ReviewListItem[];
  /**
   * The seller's User.id (NOT Seller.sellerId). When `user?.id ===
   * sellerUserId`, the "Responder" button is shown for reviews without a
   * reply. null when the viewer is anonymous OR the store is not owned
   * by the current user.
   */
  sellerUserId?: string | null;
  /** PR #1130 fix: when provided, the component fetches reviews itself. */
  sellerId?: string;
  /** Optional className override for the wrapping container. */
  className?: string;
  /**
   * Called after a successful reply submit — the parent can refetch the
   * reviews list (or optimistically update the local state).
   */
  onReplySubmitted?: (
    orderNumber: string,
    reply: SellerReply,
  ) => void;
}

export function ReviewsList({
  reviews,
  sellerUserId,
  className,
  onReplySubmitted,
}: ReviewsListProps) {
  const { user } = useAuth();
  const [dialog, setDialog] = useState<ReplyDialogState>(INITIAL_DIALOG_STATE);

  // S2-H9 — only the seller of THIS store sees the "Responder" button.
  // Anonymous viewers (user === null) + buyers + other sellers never see
  // the button (matches the spec wording "visible only when viewing
  // their own store page").
  const isOwnStore =
    user !== null && sellerUserId !== null && user.id === sellerUserId;

  const openReplyDialog = useCallback((orderNumber: string) => {
    setDialog({ orderNumber, text: '', submitting: false });
  }, []);

  const closeReplyDialog = useCallback(() => {
    // Don't close mid-submit (avoid losing the in-flight request).
    if (dialog.submitting) return;
    setDialog(INITIAL_DIALOG_STATE);
  }, [dialog.submitting]);

  const handleSubmitReply = useCallback(async () => {
    const orderNumber = dialog.orderNumber;
    if (!orderNumber) return;
    const trimmed = dialog.text.trim();
    if (trimmed.length === 0) {
      toastError.validation('la respuesta', 'Escribe tu respuesta antes de enviar.');
      return;
    }
    if (trimmed.length > REPLY_MAX_CHARS) {
      toastError.validation(
        'la respuesta',
        `No puede exceder ${REPLY_MAX_CHARS} caracteres.`,
      );
      return;
    }
    setDialog((s) => ({ ...s, submitting: true }));
    try {
      const result = await respondToReview(orderNumber, trimmed);
      toast.success('Respuesta publicada', {
        description:
          'Tu respuesta es visible públicamente debajo de la reseña del comprador.',
      });
      onReplySubmitted?.(result.orderNumber, result.sellerReply);
      setDialog(INITIAL_DIALOG_STATE);
    } catch (err: unknown) {
      if (err instanceof ApiClientError) {
        // Map the backend AppError codes to user-friendly toasts.
        switch (err.code) {
          case 'REVIEW_REPLY_ALREADY_EXISTS':
            toast.error('Ya respondiste a esta reseña.', {
              description: 'Cada reseña admite una única respuesta.',
            });
            break;
          case 'REVIEW_REPLY_WINDOW_EXPIRED':
            toast.error('El plazo para responder venció.', {
              description: 'Tienes 30 días desde la reseña para responder.',
            });
            break;
          case 'REVIEW_REPLY_NOT_YOURS':
            toast.error('No puedes responder a esta reseña.', {
              description: 'Solo el vendedor de la orden puede responder.',
            });
            break;
          case 'REVIEW_REPLY_INVALID_TEXT':
          case 'VALIDATION_ERROR':
            toast.error('La respuesta no es válida.', {
              description: err.message,
            });
            break;
          case 'REVIEW_NOT_FOUND':
          case 'ORDER_NOT_FOUND':
            toast.error('No se encontró la reseña u orden.', {
              description: 'Es posible que haya sido eliminada.',
            });
            break;
          case 'MARKET_REVIEWS_DISABLED':
            toast.error('Las reseñas no están disponibles en este momento.');
            break;
          default:
            toast.error('No se pudo enviar la respuesta.', {
              description: err.message,
            });
        }
      } else {
        toastError.network();
      }
    } finally {
      setDialog((s) => ({ ...s, submitting: false }));
    }
  }, [dialog.orderNumber, dialog.text, onReplySubmitted]);

  // PR #1130 fix: default to empty array when reviews prop is not provided
  // (when sellerId is provided, the component fetches its own reviews)
  const reviewList = reviews ?? [];
  if (reviewList.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="py-8 text-center">
          <MessageCircle
            className="mx-auto mb-2 size-8 text-muted-foreground/50"
            aria-hidden="true"
          />
          <p className="text-sm text-muted-foreground">{EMPTY_STATE_TEXT}</p>
        </CardContent>
      </Card>
    );
  }

  // ─── List rendering ─────────────────────────────────────────────────────
  return (
    <div className={className}>
      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Star className="size-5" aria-hidden="true" />
            Reseñas de compradores
            <span className="ml-1 text-sm font-normal text-muted-foreground">
              ({reviewList.length})
            </span>
          </CardTitle>
        </CardHeader>
      </Card>

      <ul className="space-y-4">
        {reviewList.map((review) => {
          const canReply = isOwnStore && !review.sellerReply;
          return (
            <li key={review.reviewId}>
              <Card>
                <CardContent className="p-4">
                  {/* Header: avatar + name + date + rating */}
                  <div className="mb-3 flex items-center gap-3">
                    <Avatar className="size-10">
                      <AvatarImage
                        src={review.fromUserAvatar ?? undefined}
                        alt={
                          review.fromUserName
                            ? `Avatar de ${review.fromUserName}`
                            : 'Avatar del comprador'
                        }
                      />
                      <AvatarFallback>
                        {initialsOf(review.fromUserName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {review.fromUserName ?? 'Comprador'}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {formatDate(review.at, { dateStyle: 'medium' })}
                        {review.productTitle ? (
                          <>
                            {' · '}
                            {review.productTitle}
                          </>
                        ) : null}
                      </p>
                    </div>
                    <StarsRow rating={review.rating} />
                  </div>

                  {/* Comment (if any) */}
                  {review.comment && (
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">
                      {review.comment}
                    </p>
                  )}

                  {/* Seller reply (if any) */}
                  {review.sellerReply && (
                    <SellerReplyBlock reply={review.sellerReply} />
                  )}

                  {/* Responder button — only for the seller of this store */}
                  {canReply && (
                    <div className="mt-3 flex justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => openReplyDialog(review.orderNumber)}
                        aria-label={`Responder a la reseña de ${review.fromUserName ?? 'el comprador'}`}
                      >
                        <MessageCircle
                          className="mr-1 size-4"
                          aria-hidden="true"
                        />
                        Responder
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </li>
          );
        })}
      </ul>

      {/* ─── Reply dialog (textarea + submit) ─── */}
      <Dialog
        open={dialog.orderNumber !== null}
        onOpenChange={(open) => {
          if (!open) closeReplyDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Responder a la reseña</DialogTitle>
            <DialogDescription>
              Tu respuesta será visible públicamente debajo de la reseña del
              comprador. Solo puedes responder una vez por reseña, dentro de
              los 30 días posteriores a su publicación.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="seller-reply-text" className="text-sm font-medium">
              Tu respuesta
            </Label>
            <Textarea
              id="seller-reply-text"
              value={dialog.text}
              onChange={(e) =>
                setDialog((s) => ({ ...s, text: e.target.value }))
              }
              placeholder="Agradece la reseña, aclara algún detalle o comparte tu versión de la transacción. Sé profesional y respetuoso."
              maxLength={REPLY_MAX_CHARS}
              rows={5}
              disabled={dialog.submitting}
              aria-label="Texto de la respuesta del vendedor"
            />
            <p className="text-[11px] text-muted-foreground">
              {dialog.text.length}/{REPLY_MAX_CHARS} caracteres. No se puede
              editar ni eliminar una vez enviada.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeReplyDialog}
              disabled={dialog.submitting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => void handleSubmitReply()}
              disabled={
                dialog.submitting || dialog.text.trim().length === 0
              }
              aria-label="Publicar respuesta"
            >
              {dialog.submitting ? (
                <Loader2
                  className="mr-1 size-4 animate-spin"
                  aria-hidden="true"
                />
              ) : (
                <MessageCircle
                  className="mr-1 size-4"
                  aria-hidden="true"
                />
              )}
              {dialog.submitting ? 'Publicando…' : 'Publicar respuesta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ReviewsList;
